import { UserRound } from "lucide-react";
import { useState } from "react";
import { cn } from "../cn";
import { initials as toInitials } from "../format";

interface AvatarProps {
  /** Display name — used as the image alt text and as the initials fallback when
   * firstName/lastName are not provided. When null/undefined/blank, a neutral
   * user-glyph placeholder is rendered instead (the "no user" state). */
  name?: string | null;
  /** First name used to derive initials. Provide alongside lastName to skip the
   * subName (tussenvoegsel). Falls back to the first word of name when omitted. */
  firstName?: string | null;
  /** Last name used to derive initials. Falls back to the last word of name when omitted. */
  lastName?: string | null;
  src?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const sizeClasses: Record<NonNullable<AvatarProps["size"]>, string> = {
  sm: "size-8 text-xs",
  md: "size-10 text-sm",
  lg: "size-12 text-base",
};

function derivedInitials(
  name: string,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  if (firstName ?? lastName) {
    return toInitials({ firstName, lastName });
  }

  const words = name.trim().split(/\s+/).filter(Boolean);

  return toInitials({
    firstName: words[0],
    lastName: words.length > 1 ? words[words.length - 1] : undefined,
  });
}

/**
 * Circular avatar that falls back to initials when no image is available,
 * and to a neutral user glyph when there is no user at all.
 */
export function Avatar({
  name,
  firstName,
  lastName,
  src,
  className,
  size = "md",
}: AvatarProps) {
  // Track which src failed instead of a boolean flag, so a later valid `src`
  // is retried automatically instead of staying stuck on the initials fallback.
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);

  if (!name?.trim()) {
    // Decorative: the adjacent text (e.g. "Niet toegewezen") carries the
    // meaning, so the glyph is hidden from assistive technology.
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground",
          sizeClasses[size],
          className,
        )}
      >
        <UserRound className="size-1/2" />
      </span>
    );
  }

  const showImage = Boolean(src) && src !== failedSrc;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-soft font-semibold text-accent-foreground ring-1 ring-border/60",
        sizeClasses[size],
        className,
      )}
    >
      {showImage ? (
        <img
          src={src}
          alt={name}
          className="size-full object-cover"
          onError={() => setFailedSrc(src)}
        />
      ) : (
        derivedInitials(name, firstName, lastName)
      )}
    </span>
  );
}
